import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useReducedMotion from '../../hooks/useReducedMotion';
import styles from './ProjectVisual.module.css';

function muteVideo(video) {
  if (!video) return;
  video.defaultMuted = true;
  video.muted = true;
}

function VideoLightbox({ media, reducedMotion, trigger, onClose }) {
  const dialogRef = useRef(null);
  const videoRef = useRef(null);
  const previousReducedMotion = useRef(reducedMotion);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const enlargedVideo = videoRef.current;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();

    function handleDocumentKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }

    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
      document.body.style.overflow = previousOverflow;
      enlargedVideo?.pause();
      if (trigger?.isConnected) trigger.focus();
    };
  }, [onClose, trigger]);

  useEffect(() => {
    if (reducedMotion && !previousReducedMotion.current) {
      videoRef.current?.pause();
    }
    previousReducedMotion.current = reducedMotion;
  }, [reducedMotion]);

  function setVideoRef(video) {
    videoRef.current = video;
    muteVideo(video);
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) onClose();
  }

  return createPortal(
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        ref={dialogRef}
        className={`${styles.dialog} ${styles.intrinsicDialog}`}
        role="dialog"
        aria-label={`${media.ariaLabel} enlarged view`}
        aria-describedby="video-lightbox-dismissal-instructions"
        aria-modal="true"
        tabIndex={-1}
      >
        <p
          id="video-lightbox-dismissal-instructions"
          className={styles.visuallyHidden}
        >
          Press Escape or click outside the video to close.
        </p>
        <video
          ref={setVideoRef}
          className={`${styles.enlargedVideo} ${styles.intrinsicVideo}`}
          src={media.src}
          poster={media.poster}
          aria-label={media.ariaLabel}
          autoPlay={!reducedMotion}
          loop={!reducedMotion}
          playsInline
          preload="metadata"
        />
      </div>
    </div>,
    document.body
  );
}

function VisualAsset({ media, reducedMotion, onOpen }) {
  const [failed, setFailed] = useState(false);
  const videoRef = useRef(null);
  const previousReducedMotion = useRef(reducedMotion);

  useEffect(() => {
    if (reducedMotion && !previousReducedMotion.current) {
      videoRef.current?.pause();
    }
    previousReducedMotion.current = reducedMotion;
  }, [reducedMotion]);

  function handleError() {
    setFailed(true);
  }

  function setVideoRef(video) {
    videoRef.current = video;
    muteVideo(video);
  }

  function openLightbox() {
    onOpen(media, videoRef.current);
  }

  function handleVideoKeyDown(event) {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    openLightbox();
  }

  if (failed) {
    return (
      <div className={styles.fallback} role="status">
        Media unavailable
      </div>
    );
  }

  if (media.type === 'image') {
    return (
      <img
        className={styles.asset}
        style={media.aspectRatio ? { aspectRatio: media.aspectRatio } : undefined}
        src={media.src}
        alt={media.alt}
        loading="lazy"
        decoding="async"
        onError={handleError}
      />
    );
  }

  if (media.type === 'video') {
    return (
      <video
        ref={setVideoRef}
        className={`${styles.asset} ${styles.interactiveAsset}`}
        style={media.aspectRatio ? { aspectRatio: media.aspectRatio } : undefined}
        src={media.src}
        poster={media.poster}
        aria-label={media.ariaLabel}
        aria-haspopup="dialog"
        autoPlay={!reducedMotion}
        loop={!reducedMotion}
        playsInline
        preload="metadata"
        role="button"
        tabIndex={0}
        onClick={openLightbox}
        onError={handleError}
        onKeyDown={handleVideoKeyDown}
      />
    );
  }

  return null;
}

export default function ProjectVisual({ media }) {
  const reducedMotion = useReducedMotion();
  const [lightbox, setLightbox] = useState(null);
  const mediaItems = Array.isArray(media) ? media : media ? [media] : [];
  const closeLightbox = useCallback(() => setLightbox(null), []);

  if (mediaItems.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.stack} ${mediaItems.length === 2 ? styles.split : ''}`}>
      {mediaItems.map((item, index) => (
        <VisualAsset
          media={item}
          reducedMotion={reducedMotion}
          onOpen={(selectedMedia, trigger) => setLightbox({ media: selectedMedia, trigger })}
          key={`${item.src || 'media'}-${index}`}
        />
      ))}
      {lightbox ? (
        <VideoLightbox
          media={lightbox.media}
          reducedMotion={reducedMotion}
          trigger={lightbox.trigger}
          onClose={closeLightbox}
        />
      ) : null}
    </div>
  );
}
